import { Fragment, useState, useImperativeHandle, useRef, forwardRef } from "react";
import { Label, FormFeedback, FormGroup } from "reactstrap";
import { useField } from "formik";
import {
  Highlighter,
  Menu,
  MenuItem,
  Typeahead,
} from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";

// App
import { FormikTypeaheadOption, FormikTypeaheadProps } from "../../../../types";
import "./Autocomplete.css";
import {
  Option,
  TypeaheadPropsAndState,
} from "react-bootstrap-typeahead/types/types";

type MapValuesToKeysIfAllowed<T> = {
  [K in keyof T]: T[K] extends PropertyKey ? K : never;
};
type Filter<T> = MapValuesToKeysIfAllowed<T>[keyof T];

function groupBy<T extends Record<string, string>, Key extends Filter<T>>(
  arr: T[],
  key: Key
): Record<T[Key], T[]> {
  return arr.reduce((accumulator, val) => {
    const groupedKey = val[key];
    if (!accumulator[groupedKey]) {
      accumulator[groupedKey] = [];
    }
    accumulator[groupedKey].push(val);
    return accumulator;
  }, {} as Record<T[Key], T[]>);
}

export type AutoCompleteRef = {
  clear: () => void;
};

const TypeaheadControl = forwardRef(({name,
  options,
  labelKey,
  defaultSelected = [],
  multiple = false,
  onChange,
  label,
  isRequired,
  disabled,
  groupByKey,...props}: FormikTypeaheadProps,ref) =>
{
  const [isFocused, setIsFocused] = useState(false);
  const [textEntered, setTextEntered] = useState<string>();

  const [, meta, helpers] = useField(name);
  const isInvalid = meta?.touched && !!meta?.error;
  const inputRef=useRef<any>(null);

  // Map defaultSelectedOptions to corresponding options
  const getDefaultSelected = (): FormikTypeaheadOption[] => {
    return defaultSelected
      ?.map((value) =>
        options.find(
          (option) =>
            (option as Record<string, string | number>)["value"] === value
        )
      )
      .filter((option) => option !== undefined) as FormikTypeaheadOption[];
  };

  const setFormikFieldValue = (selectedOption: FormikTypeaheadOption[]) => {
    const value = selectedOption?.map(
      (option) => (option as Record<string, string | number>)["value"]
    );

    helpers.setValue(value);
  };

  const handleChange = (selectedOption: FormikTypeaheadOption[]) => {
    // Check if an option is selected before updating Formik value
    if (selectedOption.length > 0) {
      setFormikFieldValue(selectedOption);
    } else {
      // Clear Formik value when no option is selected
      helpers.setValue([]);
    }
  };

  const hasDefaultSelection = getDefaultSelected().length > 0;

  const className = `form-floating ${
      textEntered || isFocused || meta?.value?.length > 0 || hasDefaultSelection  ? "focused" : ""
  }`;
  function filterBy(option: Option, state: TypeaheadPropsAndState) {
    if (state.selected.length) {
      return true;
    }
    return (
      (option as Record<string, string>)["label"]
        ?.toString()?.toLowerCase()
        .indexOf(state?.text?.toString()?.toLowerCase()) > -1
    );
  }

  useImperativeHandle(ref, () => {
    return {
      clear() {
        inputRef?.current?.clear();
      }
    };
  }, []);

  return (
    <FormGroup className={className}>
      <Typeahead
        ref={inputRef}
        id={name}
        filterBy={multiple ? undefined : filterBy}
        labelKey={labelKey}
        options={options}
        defaultSelected={getDefaultSelected()}
        multiple={multiple}
        isInvalid={isInvalid}
        disabled={disabled}
        onChange={(selected) => {
          handleChange(selected);
          if (onChange) {
            onChange(selected);
          }
        }}
        onInputChange={(text) => {
          setTextEntered(text);
        }}
        onFocus={() => {
          setIsFocused(true);
        }}
        onBlur={() => {
          setIsFocused(false);
        }}
        renderMenu={(results, menuProps, state) => {
          if (groupByKey) {
            let index = 0;
            const groups = groupBy(
              results as Record<string, string>[],
              groupByKey
            );
            const items = Object.keys(groups)
              .sort()
              ?.map((group) => (
                <Fragment key={group}>
                  {index !== 0 && <Menu.Divider />}
                  <Menu.Header>{group}</Menu.Header>
                  {groups[group]?.map((i: Option) => {
                    const item = (
                      <MenuItem key={index} option={i} position={index}>
                        <Highlighter search={state.text}>
                          {(i as Record<string, string>)["label"]}
                        </Highlighter>
                      </MenuItem>
                    );

                    index += 1;
                    return item;
                  })}
                </Fragment>
              ));

            return <Menu {...menuProps}>{items}</Menu>;
          }
          return (
            <Menu {...menuProps}>
              {results?.map((option, index) => (
                <MenuItem key={index} option={option} position={index}>
                  {(option as Record<string, string>)["label"]}
                </MenuItem>
              ))}
            </Menu>
          );
        }}
        {...props}
      />
      <Label for={name}>{isRequired ? `${label} *` : label}</Label>
      {meta?.touched && meta?.error && (
        <FormFeedback>{meta?.error}</FormFeedback>
      )}
    </FormGroup>
  );
});

export default TypeaheadControl;
